import React, { useState, useEffect, useCallback } from 'react';
import { boardAPI } from '../../../api';

type BoardType = 'community' | 'free' | 'qna';

const BOARD_TYPE_LABELS: Record<BoardType, string> = {
  community: '💬 커뮤니티',
  free: '📋 자유게시판',
  qna: '❓ Q&A',
};

interface Category {
  id: number;
  board_type: string;
  name: string;
  order: number;
  is_active: boolean;
}

const CategoryManager: React.FC = () => {
  const [boardType, setBoardType] = useState<BoardType>('community');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await boardAPI.categories(boardType);
      // 관리자용은 전체(비활성 포함) 필요하므로 별도 처리
      const res2 = await boardAPI.categoriesAll(boardType);
      setCategories(res2.data);
    } catch {
      setCategories([]);
    }
    setLoading(false);
  }, [boardType]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // 카테고리 추가
  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) { alert('카테고리명을 입력하세요.'); return; }
    try {
      await boardAPI.addCategory({ board_type: boardType, name });
      setNewName('');
      fetchCategories();
    } catch (e: any) {
      alert(e.response?.data?.error || '추가 실패');
    }
  };

  // 카테고리 삭제
  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`"${name}" 카테고리를 삭제하시겠습니까?\n해당 카테고리의 게시글은 유지됩니다.`)) return;
    try {
      await boardAPI.deleteCategory(id);
      fetchCategories();
    } catch {
      alert('삭제 실패');
    }
  };

  // 카테고리 수정
  const handleEdit = async (id: number) => {
    const name = editingName.trim();
    if (!name) { alert('카테고리명을 입력하세요.'); return; }
    try {
      await boardAPI.updateCategory(id, { name });
      setEditingId(null);
      fetchCategories();
    } catch {
      alert('수정 실패');
    }
  };

  // 활성화/비활성화 토글
  const handleToggle = async (cat: Category) => {
    try {
      await boardAPI.updateCategory(cat.id, { is_active: !cat.is_active });
      fetchCategories();
    } catch {
      alert('변경 실패');
    }
  };

  // 순서 변경 (위/아래)
  const handleOrder = async (cat: Category, direction: 'up' | 'down') => {
    const idx = categories.findIndex(c => c.id === cat.id);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= categories.length) return;
    const target = categories[targetIdx];
    try {
      await boardAPI.updateCategory(cat.id, { order: target.order });
      await boardAPI.updateCategory(target.id, { order: cat.order });
      fetchCategories();
    } catch {
      alert('순서 변경 실패');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title"><span className="icon">📂</span>카테고리 관리</div>
      </div>

      {/* 게시판 타입 탭 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {(Object.keys(BOARD_TYPE_LABELS) as BoardType[]).map(t => (
              <button key={t}
                className={`btn btn-sm ${boardType === t ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => { setBoardType(t); setEditingId(null); }}>
                {BOARD_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 카테고리 추가 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            ➕ {BOARD_TYPE_LABELS[boardType]} 카테고리 추가
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="form-control"
              placeholder="카테고리명 입력 (예: 전기기사, 소방기사...)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              style={{ fontSize: 13 }}
            />
            <button className="btn btn-primary" onClick={handleAdd} style={{ whiteSpace: 'nowrap' }}>
              추가
            </button>
          </div>
        </div>
      </div>

      {/* 카테고리 목록 */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>로딩 중...</div>
          ) : categories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              카테고리가 없습니다. 위에서 추가해보세요!
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '10px 16px', width: 60, textAlign: 'center' }}>순서</th>
                  <th style={{ padding: '10px 16px' }}>카테고리명</th>
                  <th style={{ padding: '10px 16px', width: 80, textAlign: 'center' }}>상태</th>
                  <th style={{ padding: '10px 16px', width: 160, textAlign: 'center' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, idx) => (
                  <tr key={cat.id} style={{
                    borderBottom: '1px solid var(--border)',
                    background: cat.is_active ? 'white' : '#f8fafc',
                    opacity: cat.is_active ? 1 : 0.6,
                  }}>
                    {/* 순서 */}
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <button onClick={() => handleOrder(cat, 'up')} disabled={idx === 0}
                          style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', fontSize: 12, opacity: idx === 0 ? 0.3 : 1 }}>▲</button>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{idx + 1}</span>
                        <button onClick={() => handleOrder(cat, 'down')} disabled={idx === categories.length - 1}
                          style={{ background: 'none', border: 'none', cursor: idx === categories.length - 1 ? 'default' : 'pointer', fontSize: 12, opacity: idx === categories.length - 1 ? 0.3 : 1 }}>▼</button>
                      </div>
                    </td>

                    {/* 카테고리명 */}
                    <td style={{ padding: '8px 16px' }}>
                      {editingId === cat.id ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input className="form-control"
                            value={editingName}
                            onChange={e => setEditingName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleEdit(cat.id)}
                            style={{ fontSize: 13, height: 32 }}
                            autoFocus
                          />
                          <button className="btn btn-primary btn-sm" onClick={() => handleEdit(cat.id)}>저장</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>취소</button>
                        </div>
                      ) : (
                        <span style={{ fontWeight: 600 }}>{cat.name}</span>
                      )}
                    </td>

                    {/* 상태 */}
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      <button onClick={() => handleToggle(cat)}
                        style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: cat.is_active ? '#d5f5e3' : '#fdecea',
                          color: cat.is_active ? '#1e8449' : '#c0392b',
                          fontWeight: 700,
                        }}>
                        {cat.is_active ? '활성' : '비활성'}
                      </button>
                    </td>

                    {/* 관리 버튼 */}
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button className="btn btn-ghost btn-sm"
                          onClick={() => { setEditingId(cat.id); setEditingName(cat.name); }}
                          style={{ fontSize: 12 }}>✏️ 수정</button>
                        <button onClick={() => handleDelete(cat.id, cat.name)}
                          style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}>
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
        💡 비활성화된 카테고리는 게시판에 표시되지 않지만 기존 게시글은 유지됩니다.
      </div>
    </div>
  );
};

export default CategoryManager;
